/**
 * Protected Route Wrapper — Clerk Auth Guard
 *
 * Guards authenticated routes by checking auth state.
 * - Loading: Shows full-page spinner
 * - Unauthenticated: Redirects to /login with URL preservation
 * - Authenticated: Renders children normally
 */

import { ReactNode } from 'react';
import { useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { clerkPublishableKey } from '@/lib/auth';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const [location, setLocation] = useLocation();

  // CI/test mode: no Clerk key configured, skip browser auth guard.
  // Server-side JWT validation still applies to all API requests.
  if (!clerkPublishableKey) {
    return <>{children}</>;
  }

  // Show loading spinner while checking auth state
  if (!isLoaded) {
    return (
      <div
        data-testid="auth-loading"
        className="flex min-h-screen items-center justify-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Redirect unauthenticated users to /login with return URL preserved
  if (!isSignedIn) {
    const redirect = encodeURIComponent(location);
    setLocation(`/login?redirect=${redirect}`);
    return null;
  }

  // Authenticated - render children
  return <>{children}</>;
}
